// Libraries
import { useState } from "react";
import { Box, ClickAwayListener, IconButton, Paper, SvgIcon, Tooltip } from "@mui/material";
import clsx from "clsx";

// Types
import { TActionToDo, TDisplayReason } from "../../../types/dealerTrades";

// Constants
import { APPROVE_REJECT, CONFIRM_DECLINE, EActionType } from "../../../constants";

// Icons
import { ReactComponent as EditAssetSwap } from "@/assets/icons/dataPages/inventory/edit-asset-swap.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";
import FlakyIcon from "@mui/icons-material/Flaky";

// Components
import ChoiceComponent from "./ChoiceComponent";
import ReasonCard from "./ReasonCard";

type TApproveComponentProps = {
	optionsCustomComponent: {
		delete: {
			disabled: boolean;
			isBlocked: boolean;
			onClick: ({ status, reason }: { status: string; reason: string }) => void;
		};
		editAssetSwap: {
			hidden: boolean;
			disabled: boolean;
			onClick: () => void;
		};
		approval: {
			disabledActions: {
				approve: boolean;
				reject: boolean;
			};
			disabled: boolean;
			onClick: ({ status, reason }: { status: string; reason?: string }) => void;
		};
		confirmation: {
			disabled: boolean;
			onClick: ({ status, reason }: { status: string; reason?: string }) => void;
		};
	};
	amountSelected: number;
};

export default function ApproveComponent(props: Readonly<TApproveComponentProps>) {
	const { optionsCustomComponent, amountSelected } = props;
	const {
		delete: deleteOptions,
		editAssetSwap: editAssetSwapOptions,
		approval: approvalOptions,
		confirmation: confirmationOptions,
	} = optionsCustomComponent;

	const [openOption, setOpenOption] = useState<TActionToDo>("");
	const [displayReason, setDisplayReason] = useState<TDisplayReason>({ show: false });

	function onClickWayHandle() {
		setOpenOption("");
		setDisplayReason({ show: false });
	}

	function onApproveHandle() {
		approvalOptions.onClick({ status: EActionType.APPROVE });
		onClickWayHandle();
	}

	function onRejectHandle(reason: string) {
		approvalOptions.onClick({ status: EActionType.REJECT, reason });
		onClickWayHandle();
	}

	function onConfirmHandle() {
		confirmationOptions.onClick({ status: EActionType.CONFIRM });
		onClickWayHandle();
	}

	function onCancelConfirmationHandle(reason: string) {
		deleteOptions.onClick({ status: EActionType.CANCEL_CONFIRMATION, reason });
		onClickWayHandle();
	}

	function onCancelHandle(reason: string) {
		deleteOptions.onClick({ status: EActionType.CANCEL, reason });
		onClickWayHandle();
	}

	function onDisplayReason() {
		setDisplayReason((prev) => ({ ...prev, show: true, action: "rejection" }));
	}

	return (
		<Box className="flex">
			<ChoiceComponent
				// Options
				key={CONFIRM_DECLINE}
				icon={<FlakyIcon sx={{ "&:hover": { color: "var(--primary-color)" } }} />}
				textApprove="Confirm"
				textCancel="Decline"
				amountSelected={amountSelected}
				displayReason={displayReason}
				isOpen={openOption === CONFIRM_DECLINE}
				disabled={confirmationOptions.disabled}
				hidden={!approvalOptions.disabled || confirmationOptions.disabled}
				// Callbacks
				setOpenOption={() => setOpenOption(CONFIRM_DECLINE)}
				onClickWayHandle={onClickWayHandle}
				onApproveHandle={onConfirmHandle}
				onCancelHandle={onCancelConfirmationHandle}
				onDisplayReason={onDisplayReason}
			/>

			<ChoiceComponent
				// Options
				key={APPROVE_REJECT}
				icon={<FlakyIcon sx={{ "&:hover": { color: "var(--primary-color)" } }} />}
				textApprove="Approve"
				textCancel="Reject"
				amountSelected={amountSelected}
				displayReason={displayReason}
				isOpen={openOption === APPROVE_REJECT}
				disabled={approvalOptions.disabled}
				disabledButtons={{
					approve: approvalOptions.disabledActions.approve ?? false,
					cancel: approvalOptions.disabledActions.reject ?? false,
				}}
				hidden={!confirmationOptions.disabled}
				// Callbacks
				setOpenOption={() => setOpenOption(APPROVE_REJECT)}
				onClickWayHandle={onClickWayHandle}
				onApproveHandle={onApproveHandle}
				onCancelHandle={onRejectHandle}
				onDisplayReason={onDisplayReason}
			/>

			{!editAssetSwapOptions.hidden && (
				<IconButton
					size="small"
					className={clsx({ "w-[35px] !ml-[18px]": true, "opacity-[0.4]": editAssetSwapOptions.disabled })}
					onClick={editAssetSwapOptions.onClick}
					disabled={editAssetSwapOptions.disabled}
				>
					<SvgIcon className="pt-[2px] !w-[26px] !h-[26px]" component={EditAssetSwap} />
				</IconButton>
			)}

			{openOption === "Cancel" && (
				<ClickAwayListener onClickAway={() => onClickWayHandle()}>
					<Box className="relative">
						<ReasonCard
							amountSelected={amountSelected}
							className="top-[40px] right-[-54px]"
							buttonText={"Delete"}
							onClick={onCancelHandle}
							onClickAway={() => onClickWayHandle()}
						/>
					</Box>
				</ClickAwayListener>
			)}

			{confirmationOptions.disabled && (
				<Tooltip title={"GPS device transfer is in progress"} arrow disableHoverListener={!deleteOptions.isBlocked}>
					<Paper aria-disabled={deleteOptions.isBlocked} elevation={0}>
						<IconButton
							size="small"
							className="w-[35px] !ml-[18px]"
							onClick={() => {
								setOpenOption("Cancel");
								setDisplayReason((prev) => ({ ...prev, show: true, action: "cancellation" }));
							}}
							disabled={deleteOptions.isBlocked || deleteOptions.disabled}
						>
							<SvgIcon className="pl-[4px] pt-[2px] !w-[28px] !h-[28px]" component={TrashIcon} />
						</IconButton>
					</Paper>
				</Tooltip>
			)}
		</Box>
	);
}
